import { useEffect, useState } from "react";
import { useAuth, useToast } from "../../../hooks";
import { NOTIFICATION_TYPES } from "../../../utils";
import { Avatar, Button, Label, Spinner, TextInput } from "flowbite-react";
import { HiPencil, HiTrash } from "react-icons/hi";

export const ProfileSettings = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    apiClient,
    fetchUserDetails,
    user: profileData,
    userLoading: isLoading,
  } = useAuth();
  const showToast = useToast();

  useEffect(() => {
    fetchUserDetails();
  }, [fetchUserDetails]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    try {
      const data = {
        first_name: e.target.firstName.value,
        last_name: e.target.lastName.value,
        timeozone: e.target.timeozone.value,
        language: e.target.language.value,
      };
      await apiClient.patch("/users/me/", data);
      showToast({
        content: `Profile settings updated successfully`,
        type: NOTIFICATION_TYPES.success,
      });
    } catch (err) {
      showToast({
        content: `Failed to update profile settings ${err}`,
        type: NOTIFICATION_TYPES.error,
      });
      console.error(`Failed to update profile settings`, err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-[32rem] w-full items-center justify-center">
        <Spinner aria-label="Extra large spinner example" size="xl" />
      </div>
    );
  }

  return (
    <div className="flex min-h-full flex-1 flex-col">
      <div className="animation-slide-up-lil-bounce mt-1 flex w-full flex-1 flex-col items-stretch justify-start overflow-x-auto rounded-lg bg-white p-4 shadow">
        <div className="mt-4 flex w-full items-center justify-between rounded-md border border-gray-200 p-4">
          <Avatar
            img="https://github.com/mohitkyadav.png"
            alt="avatar of mohit"
            rounded
            size="xl"
          />
          <div className="flex flex-row gap-1">
            <Button className="ml-4" color="blue">
              <HiPencil className="mr-2 h-5 w-5" />
              Change Avatar
            </Button>
            <Button className="ml-4" color="failure">
              <HiTrash className="mr-2 h-5 w-5" />
              Remove Avatar
            </Button>
          </div>
        </div>
        <form
          className="mt-4 flex w-full flex-col gap-4 rounded-md border border-gray-200 p-4"
          onSubmit={handleSubmit}
        >
          <div className="flex items-center gap-2">
            <div className="max-w-md flex-1">
              <div className="mb-2 block">
                <Label htmlFor="username" value="Username" />
              </div>
              <TextInput
                id="username"
                type="text"
                required
                shadow
                disabled
                value={profileData?.username}
              />
            </div>
            <div className="max-w-md flex-1">
              <div className="mb-2 block">
                <Label htmlFor="email" value="Email" />
              </div>
              <TextInput
                id="email"
                required
                shadow
                value={profileData?.email}
                type="email"
                placeholder="name@provider.com"
                disabled
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="max-w-md flex-1">
              <div className="mb-2 block">
                <Label htmlFor="firstName" value="First Name" />
              </div>
              <TextInput
                id="firstName"
                type="text"
                shadow
                defaultValue={profileData?.first_name}
                disabled={isSubmitting}
              />
            </div>
            <div className="max-w-md flex-1">
              <div className="mb-2 block">
                <Label htmlFor="lastName" value="Last Name" />
              </div>
              <TextInput
                id="lastName"
                type="text"
                shadow
                defaultValue={profileData?.last_name}
                disabled={isSubmitting}
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="max-w-md flex-1">
              <div className="mb-2 block">
                <Label htmlFor="language" value="Language" />
              </div>
              <TextInput
                id="language"
                type="text"
                shadow
                defaultValue={profileData?.language}
                disabled={isSubmitting}
              />
            </div>
            <div className="max-w-md flex-1">
              <div className="mb-2 block">
                <Label htmlFor="timeozone" value="Timezone" />
              </div>
              <TextInput
                id="timeozone"
                type="text"
                shadow
                defaultValue={profileData?.timezone}
                disabled={isSubmitting}
              />
            </div>
          </div>
          <Button
            className="max-w-24"
            type="submit"
            color="blue"
            disabled={isSubmitting || !profileData || isLoading}
            isProcessing={isSubmitting}
          >
            Save
          </Button>
        </form>
      </div>
    </div>
  );
};
